import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';

import type { DynamicTextConfig } from '@shared/components/dynamic-text/dynamic-text.types';
import { Dynamic } from '@shared/registry/dynamic';

import { type DetailExpandableCardConfig } from './detail-expandable-card.types';

@Component({
  selector: 'app-detail-expandable-card',
  templateUrl: './detail-expandable-card.html',
  imports: [Dynamic],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailExpandableCard {
  private readonly sanitizer = inject(DomSanitizer);

  readonly config = input.required<DetailExpandableCardConfig>();

  protected readonly isCollapsible = computed(() => {
    const config = this.config();
    return !!config.title && !!config.expandButton && !!config.collapseButton;
  });
  protected readonly isExpanded = signal(false);

  protected readonly containerBackground = computed(() => {
    return 'linear-gradient(135deg, var(--gradients-tertiary-start), var(--gradients-tertiary-middle), var(--gradients-tertiary-stop))';
  });

  protected readonly titleProps = computed(() => ({
    config: {
      text: this.config().title,
      size: 'xl' as const,
      fontWeight: 'semibold' as const,
      cssVarPrefix: 'detailexpandablecard-content',
    } as DynamicTextConfig,
  }));

  protected readonly buttonProps = computed(() => {
    const config = this.config();
    return {
      config: this.isExpanded() ? config.collapseButton : config.expandButton,
    };
  });

  protected readonly sanitizedContent = computed((): SafeHtml => {
    return this.sanitizer.bypassSecurityTrustHtml(this.config().expandedContent);
  });

  protected toggle(): void {
    this.isExpanded.update((value) => !value);
  }
}
